import React, { memo } from 'react';
import { styled } from '@compiled/react';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	useIssueSearchActions,
	useTotalIssueCount,
	useTotalIssueCountIsError,
	useTotalIssueCountIsFetching,
} from '../../../services/issue-search/selectors.tsx';
import LoadUncappedCountButton from './load-uncapped-count-button/index.tsx';
import messages from './messages.tsx';

export type Props = {
	/**
	 * A message descripter containing any text that should be prefixed to the total count.
	 * Example - a message containing page ranges:
	 * ```
	 * {
	 *  id: 'issue-navigator.card-container.paginator.issue-range-known',
	 *  defaultMessage: '{startIssueRange}-{endIssueRange} of {totalNumberIssues}',
	 *  description: 'Text indicating the shown range out of the total number of issues.',
	 * }
	 * ```
	 */
	prependMessage: MessageDescriptorV2;
	/**
	 * Any values contained within `prependMessage` prop.
	 *
	 * **NOTE - a value with key `totalNumberIssues` will automatically be appended to this prop.**
	 *
	 * Example - Given this `prependMessage` prop:
	 * ```
	 * {
	 *  id: 'issue-navigator.card-container.paginator.issue-range-known',
	 *  defaultMessage: '{startIssueRange}-{endIssueRange} of {totalNumberIssues}',
	 *  description: 'Text indicating the shown range out of the total number of issues.',
	 * }
	 * ```
	 *
	 * `prependMessageValues` prop would look like this:
	 *
	 * ```
	 * {
	 *   startIssueRange: 1,
	 *   endIssueRange: 50
	 * }
	 * ```
	 *
	 * This component will then mutate the prop by adding the total count:
	 * ```
	 * {
	 *   startIssueRange: 1,
	 *   endIssueRange: 50,
	 *   totalNumberIssues: '1000+'
	 * }
	 * ```
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	prependMessageValues: Record<string, any>;
	/**
	 * Indicates if the `stableCount` prop is capped. When a count is capped, the user will
	 * be given the option of loading the uncapped count
	 *
	 * TODO - Refactor this component to use relay fragments and this prop can be replaced with fragment data
	 */
	isCapped: boolean;
	/**
	 * Contains the (possibly capped) total count. This value is derived from the issueSearchStable query
	 *
	 * TODO - Refactor this component to use relay fragments and this prop can be replaced with fragment data
	 */
	stableCount: number;
};

const TotalIssueCount = ({
	prependMessage,
	prependMessageValues,
	isCapped,
	stableCount,
}: Props) => {
	const { formatMessage } = useIntl();

	const totalIssueCount = useTotalIssueCount();
	const totalIssueCountIsFetching = useTotalIssueCountIsFetching();
	const totalIssueCountIsError = useTotalIssueCountIsError();
	const { fetchTotalIssueCount } = useIssueSearchActions();

	if (isCapped) {
		if (totalIssueCountIsError) {
			return (
				<>
					<Box as="span">
						{formatMessage(prependMessage, {
							...prependMessageValues,
							totalNumberIssues: `${stableCount}+`,
						})}
					</Box>
					<Tooltip
						position="top"
						content={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.errorTooltipTextIssueTermRefresh
								: messages.errorTooltipText,
						)}
					>
						{(tooltipProps) => (
							<ErrorIconContainer
								{...tooltipProps}
								data-testid="issue-navigator.common.ui.total-issue-count.error-icon"
							>
								<ErrorIcon
									spacing="spacious"
									label={formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.errorIconLabelIssueTermRefresh
											: messages.errorIconLabel,
									)}
								/>
							</ErrorIconContainer>
						)}
					</Tooltip>
					<FireUiAnalytics
						action="viewed"
						actionSubject="errorIcon"
						actionSubjectId="totalIssueCount"
					/>
				</>
			);
		}

		if (totalIssueCountIsFetching) {
			return (
				<>
					<Box as="span">
						{formatMessage(prependMessage, {
							...prependMessageValues,
							totalNumberIssues: '',
						})}
					</Box>
					<SpinnerContainer>
						<Spinner size="small" testId="issue-navigator.common.ui.total-issue-count.spinner" />
					</SpinnerContainer>
				</>
			);
		}

		if (!totalIssueCount) {
			return (
				<Box as="span">
					{formatMessage(prependMessage, {
						...prependMessageValues,
						totalNumberIssues: (
							<LoadUncappedCountButton
								displayText={`${stableCount}+`}
								onFetchCount={fetchTotalIssueCount}
							/>
						),
					})}
				</Box>
			);
		}

		return (
			<Box as="span">
				{formatMessage(prependMessage, {
					...prependMessageValues,
					totalNumberIssues: totalIssueCount,
				})}
			</Box>
		);
	}

	return (
		<Box as="span">
			{formatMessage(prependMessage, {
				...prependMessageValues,
				totalNumberIssues: stableCount,
			})}
		</Box>
	);
};

export default memo(TotalIssueCount);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorIconContainer = styled.span({
	display: 'inline-block',
	height: '24px', // For vertical alignment with total count value

	color: token('color.text.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.span({
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	'margin-bottom': '3px', // For vertical alignment with total count value
	'margin-left': token('space.050'), // adds space between count text and the spinner. E.g. 1-50 of <spinner>
});
